/**
 * Pinch-to-Zoom Gesture Handler
 * Handles pinch-to-zoom gestures on touch devices
 */

class PinchZoomHandler {
  constructor (element, onZoomChange, onPinchEnd, getCurrentZoomLevel) {
    this.element = element
    this.onZoomChange = onZoomChange
    this.onPinchEnd = onPinchEnd
    this.getCurrentZoomLevel = getCurrentZoomLevel

    // Touch state
    this.touches = []
    this.initialDistance = 0
    this.initialScale = 1.0
    this.currentScale = 1.0
    this.initialCenter = { x: 0.5, y: 0.5 }

    // Gesture state
    this.isPinching = false
    this.lastTouchTime = 0

    // Bind methods
    this.handleTouchStart = this.handleTouchStart.bind(this)
    this.handleTouchMove = this.handleTouchMove.bind(this)
    this.handleTouchEnd = this.handleTouchEnd.bind(this)

    // Initialize
    this.initialize()
  }

  /**
   * Initialize event listeners
   */
  initialize () {
    if (!this.element) return

    // Add touch event listeners
    this.element.addEventListener('touchstart', this.handleTouchStart, { passive: false })
    this.element.addEventListener('touchmove', this.handleTouchMove, { passive: false })
    this.element.addEventListener('touchend', this.handleTouchEnd, { passive: false })

    // Prevent default touch behaviors that might interfere
    this.element.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        e.preventDefault()
      }
    }, { passive: false })

    this.element.addEventListener('touchmove', (e) => {
      if (e.touches.length === 2) {
        e.preventDefault()
      }
    }, { passive: false })
  }

  /**
   * Handle touch start event
   * @param {TouchEvent} event - Touch event
   */
  handleTouchStart (event) {
    const touches = Array.from(event.touches)

    // Only handle two-finger touches
    if (touches.length !== 2) {
      this.resetPinchState()
      return
    }

    this.touches = touches
    this.initialDistance = this.getDistance(touches[0], touches[1])

    // Get the current zoom level from the video zoom instance if available
    if (this.getCurrentZoomLevel) {
      this.initialScale = this.getCurrentZoomLevel()
    } else {
      this.initialScale = this.currentScale
    }

    this.initialCenter = this.getCenter(touches[0], touches[1])
    this.isPinching = true
    this.lastTouchTime = Date.now()

    console.log('Pinch gesture started with initial scale:', this.initialScale)
  }

  /**
   * Handle touch move event
   * @param {TouchEvent} event - Touch event
   */
  handleTouchMove (event) {
    if (!this.isPinching || event.touches.length !== 2) {
      return
    }

    const touches = Array.from(event.touches)
    const currentDistance = this.getDistance(touches[0], touches[1])
    const currentCenter = this.getCenter(touches[0], touches[1])

    // Calculate scale change with smoothing
    if (this.initialDistance > 0) {
      const rawScale = currentDistance / this.initialDistance

      // Apply smoothing to prevent jerky movement
      const smoothingFactor = 0.8
      const smoothedScale = (rawScale * (1 - smoothingFactor)) + (this.currentScale * smoothingFactor)

      // Clamp scale to reasonable bounds to prevent extreme zoom
      const clampedScale = Math.max(0.1, Math.min(5.0, smoothedScale))

      this.currentScale = clampedScale

      // Calculate center point relative to element
      const rect = this.element.getBoundingClientRect()
      const centerX = Math.max(0, Math.min(1, (currentCenter.x - rect.left) / rect.width))
      const centerY = Math.max(0, Math.min(1, (currentCenter.y - rect.top) / rect.height))

      // Call zoom change callback
      if (this.onZoomChange) {
        this.onZoomChange(this.currentScale, centerX, centerY)
      }
    }
  }

  /**
   * Handle touch end event
   * @param {TouchEvent} event - Touch event
   */
  handleTouchEnd (event) {
    if (this.isPinching) {
      console.log('Pinch gesture ended')
      this.isPinching = false

      // Notify that pinch gesture has ended
      if (this.onPinchEnd) {
        this.onPinchEnd()
      }
    }

    // Reset if no touches remain
    if (event.touches.length === 0) {
      this.resetPinchState()
    }
  }

  /**
   * Calculate distance between two touch points
   * @param {Touch} touch1 - First touch point
   * @param {Touch} touch2 - Second touch point
   * @returns {number} Distance between points
   */
  getDistance (touch1, touch2) {
    const dx = touch1.clientX - touch2.clientX
    const dy = touch1.clientY - touch2.clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  /**
   * Calculate center point between two touch points
   * @param {Touch} touch1 - First touch point
   * @param {Touch} touch2 - Second touch point
   * @returns {Object} Center point {x, y}
   */
  getCenter (touch1, touch2) {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    }
  }

  /**
   * Reset pinch state
   */
  resetPinchState () {
    this.touches = []
    this.initialDistance = 0
    this.initialScale = 1.0
    this.currentScale = 1.0
    this.initialCenter = { x: 0.5, y: 0.5 }
    this.isPinching = false
  }

  /**
   * Check if currently pinching
   * @returns {boolean} True if pinching
   */
  isCurrentlyPinching () {
    return this.isPinching
  }

  /**
   * Get current scale
   * @returns {number} Current scale
   */
  getCurrentScale () {
    return this.currentScale
  }

  /**
   * Clean up event listeners
   */
  destroy () {
    if (!this.element) return

    this.element.removeEventListener('touchstart', this.handleTouchStart)
    this.element.removeEventListener('touchmove', this.handleTouchMove)
    this.element.removeEventListener('touchend', this.handleTouchEnd)

    this.resetPinchState()
    this.element = null
    this.onZoomChange = null

    console.log('Pinch zoom handler unmounted')
  }
}

export default PinchZoomHandler
